import { useState, useEffect } from "react";
import useCustomer from "hooks/useCustomer";
import usePaymentMethodsSwr from "hooks/usePaymentMethodsSwr";

export default function usePaymentGateway(customerId, pgatewayId) {
    const [resolvedGatewayId, setResolvedGatewayId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    const { customerProfile, error: customerError } = useCustomer(customerId);
    const { paymentMethods = [] } = usePaymentMethodsSwr();

    useEffect(() => {
        const determineGateway = () => {
            // =====================================================
            // 1) SI LLEGA PGATEWAYID — USAMOS EL CODE SIN MODIFICARLO
            // =====================================================

            if (pgatewayId && paymentMethods?.length) {
                const paymentMethod = paymentMethods.find((m) => m.id === pgatewayId);

                if (!paymentMethod) {
                    setErrorMessage("Invalid payment method.");
                    return;
                }

                const code = paymentMethod.code;

                // 🔹 PayPal Express
                if (code === "paypal_express") {
                    setErrorMessage(
                        "The previous payment was processed using PayPal Express, which does not support additional charges. Please use a credit card to complete the transaction."
                    );
                    return;
                }

                // 🔹 PayPal Pro
                if (code === "paypal_pro" && !customerProfile?.payflowProPnref) {
                    setErrorMessage(
                        "PayPal Pro information is required to process this payment. Please update the payment method."
                    );
                    return;
                }

                // 🔹 Authorize.Net
                if (
                    code === "authorize_net" &&
                    !(
                        customerProfile?.authorizeNetCustomerProfileId ||
                        customerProfile?.authorizeNetPaymentProfileId
                    )
                ) {
                    setErrorMessage(
                        "This order was processed as a direct charge without generating a reusable payment token. As a result, the payment cannot be modified."
                    );
                    return;
                }

                // 🔹 Stripe
                if (
                    code === "stripe" &&
                    !(customerProfile?.stripeCustomerId && customerProfile?.stripePaymentMethodId)
                ) {
                    setErrorMessage(
                        "Stripe information is required to process this payment. Please update the payment method."
                    );
                    return;
                }

                // Todo OK → devolvemos el code tal cual viene
                setResolvedGatewayId(code);
                return;
            }

            // =====================================================
            // 2) FALLBACK — NO LLEGÓ PGATEWAYID
            //    (ÓRDENES ANTIGUAS QUE NO ALMACENABAN ID)
            // =====================================================

            if (customerError) {
                setErrorMessage("Error loading customer profile.");
                return;
            }

            if (!customerProfile) {
                setErrorMessage("Customer profile not found.");
                return;
            }

            // tokens existentes → determinan el gateway automáticamente
            const hasAuthorizeNet =
                customerProfile?.authorizeNetCustomerProfileId ||
                customerProfile?.authorizeNetPaymentProfileId;

            const hasPayPalPro = customerProfile?.payflowProPnref;

            const hasStripe =
                customerProfile?.stripeCustomerId && customerProfile?.stripePaymentMethodId;

            if (hasAuthorizeNet) {
                setResolvedGatewayId("authorize_net");
                return;
            }

            if (hasPayPalPro) {
                setResolvedGatewayId("paypal_pro");
                return;
            }

            if (hasStripe) {
                setResolvedGatewayId("stripe");
                return;
            }

            // Ningún token detectado
            setErrorMessage(
                "No prior authorization found for a charge. Please proceed with entering the card details to complete the transaction."
            );
            setResolvedGatewayId(null);
        };

        determineGateway();
    }, [pgatewayId, paymentMethods, customerProfile, customerError]);

    return { resolvedGatewayId, errorMessage };
}
